import React, { useState, useEffect } from 'react';
import { adminManagementService } from '../../services/adminService';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, UserMinus, CheckCircle, XCircle, Download, ExternalLink } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';

function AdminUsers() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page')) || 1;

    const [data, setData] = useState({ data: [], total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isVerified, setIsVerified] = useState('');

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            if (searchTerm !== debouncedSearch) {
                setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    return params;
                });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset page when filter changes
    const handleFilterChange = (val) => {
        setIsVerified(val);
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('page', '1');
            return params;
        });
    };

    useEffect(() => {
        loadUsers();
    }, [currentPage, debouncedSearch, isVerified]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const response = await adminManagementService.getUsers({
                page: currentPage,
                limit: 20,
                search: debouncedSearch,
                isVerified: isVerified
            });
            setData(response);
        } catch (error) {
            console.error('Failed to load users', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user? This action is permanent.')) return;
        try {
            await adminManagementService.deleteUser(id);
            loadUsers(); // Reload to get fresh data/counts
        } catch (error) {
            alert('Failed to delete user');
        }
    };

    const users = data.data || [];

    const handleExport = async () => {
        try {
            const blob = await adminManagementService.exportUsers({ search: searchTerm });
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('Failed to export users', error);
            alert('Failed to export users');
        }
    };

    if (loading) return <div className="admin-loading">Loading users...</div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h2>User Management ({data.total})</h2>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="admin-search-bar">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        className="admin-select"
                        value={isVerified}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #334155', background: '#1e293b', color: 'white' }}
                    >
                        <option value="">All Statuses</option>
                        <option value="true">Verified</option>
                        <option value="false">Unverified</option>
                    </select>
                    <button className="btn btn-secondary" onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Download size={18} />
                        Export CSV
                    </button>
                </div>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Verified</th>
                            <th>Joined</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.id}>
                                <td>
                                    <div className="user-cell">
                                        <div className="user-avatar-sm">
                                            {user.fullName?.[0] || user.email?.[0] || 'U'}
                                        </div>
                                        <span>{user.fullName || 'No Name'}</span>
                                    </div>
                                </td>
                                <td>{user.email}</td>
                                <td>
                                    {user.isEmailVerified ? (
                                        <span className="status-badge success"><CheckCircle size={12} /> Verified</span>
                                    ) : (
                                        <span className="status-badge warning"><XCircle size={12} /> Pending</span>
                                    )}
                                </td>
                                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                                <td>
                                    <div className="action-row" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <Link to={`/admin/users/${user.id}`} className="admin-action-link" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <ExternalLink size={16} /> Details
                                        </Link>
                                        <button className="icon-btn delete" onClick={() => handleDelete(user.id)} title="Delete User">
                                            <UserMinus size={18} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={data.totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default AdminUsers;
